import { Fragment, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useEffectOnce } from 'react-use';
import { PanelControls } from '~/components/ui/PanelControls';
import { ExchangeState, rage, useInputAction, useObservable } from '~/rage';
import { InputAction, Layouts } from '~/shared';
import { Exchange } from '../../components/Exchange';
import { FinishScreen } from '../../components/FinishScreen';
import { Head } from '../../components/Head';
import { PlayerSelect } from '../../components/PlayerSelect';
import { PlayerWait } from '../../components/PlayerWait';
import {
	resetStore,
	setSelectedPlayer,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledContainer,
	StyledPanelControlsContainer,
} from './PropertyExchange.styled';

const PropertyExchange: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const playerList = useLayoutSelector(
		(state) => state.propertyExchange.playerList,
	);
	const playerIndex = useLayoutSelector(
		(state) => state.propertyExchange.playerIndex,
	);
	const propertyList = useLayoutSelector(
		(state) => state.propertyExchange.propertyList,
	);
	const propertyListIndex = useLayoutSelector(
		(state) => state.propertyExchange.propertyListIndex,
	);
	const selectedPlayer = useLayoutSelector(
		(state) => state.propertyExchange.selectedPlayer,
	);
	const finishScreenVisible = useLayoutSelector(
		(state) => state.propertyExchange.finishScreenVisible,
	);
	const waitingForInvite = useLayoutSelector(
		(state) => state.propertyExchange.waitingForInvite,
	);
	const exchangeState = useObservable(rage.exchange.getExchangeState());
	// @TODO: состояние ожидания игрока

	const leftControls = useMemo(() => {
		if (selectedPlayer) {
			return [
				{
					text: 'Управление',
					keys: ['ArrowUp', 'ArrowDown'],
				},
				{
					text: 'Добавить в обмен',
					keys: ['Enter'],
					onClick: async () => {
						await rage.exchange.placeItem(propertyList[propertyListIndex].id);
					},
				},
			];
		}

		return [
			{
				text: 'Управление',
				keys: ['ArrowUp', 'ArrowDown'],
			},
			{
				text: 'Выбрать игрока',
				keys: ['Enter'],
				onClick: async () => {
					await rage.exchange.invite(playerList[playerIndex].characterId);
					dispatch(setSelectedPlayer(playerList[playerIndex]));
				},
			},
		];
	}, [
		selectedPlayer,
		playerList,
		playerIndex,
		propertyList,
		propertyListIndex,
	]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: async () => {
				await rage.exchange.exitExchange();
			},
		},
	];

	const showControls = useMemo(
		() =>
			exchangeState !== ExchangeState.ConfirmingExchange && !waitingForInvite,
		[exchangeState, waitingForInvite],
	);

	useInputAction(InputAction.CommonMenuClose, async () => {
		await rage.exchange.exitExchange();
	});

	useEffectOnce(() => {
		dispatch(resetStore());
	});

	return (
		<StyledContainer className={Layouts.PropertyExchange}>
			{!waitingForInvite && (
				<>
					{exchangeState === ExchangeState.Completed ? (
						<PlayerSelect />
					) : (
						<Fragment>
							<Head />
							<Exchange />
						</Fragment>
					)}
				</>
			)}
			<CSSTransition
				timeout={300}
				in={waitingForInvite}
				classNames={'wait'}
				unmountOnExit
				appear
			>
				<PlayerWait />
			</CSSTransition>
			<StyledPanelControlsContainer
				style={{
					opacity: showControls ? 1 : 0,
				}}
			>
				<PanelControls controls={leftControls} fullScreenLayout />
				<PanelControls controls={rightControls} fullScreenLayout />
			</StyledPanelControlsContainer>
			<CSSTransition
				timeout={300}
				in={finishScreenVisible}
				classNames={'finish'}
				unmountOnExit
				appear
			>
				<FinishScreen />
			</CSSTransition>
		</StyledContainer>
	);
};

export { PropertyExchange };
